import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Draggable from 'react-draggable';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, InputGroup } from 'reactstrap';
import { createReportPlan } from '../../../slices/veloxity/audience/ReportDownloadReducer';
import { showSuccessAlert, showErrorAlert } from '../../custom/sweetalertHelper';
import { allCountries } from 'country-region-data';
import Select from 'react-select/async';
import suggestions from '../../../assets/data/audience/suggestions.json';

const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Turkish' },
  { value: 'es', label: 'Spanish' },
];

const poiTypeOptions = [
  { value: 'SUPERMARKET', label: 'SuperMarket' },
  { value: 'MALL', label: 'Mall' },
  { value: 'HOSPITAL', label: 'Hospital' },
  { value: 'SCHOOL', label: 'School' },
  { value: 'UNIVERSITY', label: 'University' },
  { value: 'DEPARTMENT_STORE', label: 'Department Store' },
  { value: 'CAFES', label: 'Cafes' },
  { value: 'RESTAURANT', label: 'Restaurants' },
  { value: 'BANK', label: 'Bank' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'HOTEL_MARINA', label: 'Hotel Marina' },
  { value: 'BEACH', label: 'Beach' },
  { value: 'AIRPORT', label: 'Airport' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'FUEL', label: 'Fuel' },
  { value: 'CHARGING_STATION', label: 'Charging Station' },
];

const ReportCreateModal = ({ isOpen, toggle, selectedAudience, updateLoading }) => {
  const dispatch = useDispatch();
  const [reportName, setReportName] = useState('');
  const [language, setLanguage] = useState('en');
  const [poiType, setPoiType] = useState('SUPERMARKET');
  const [poiNames, setPoiNames] = useState([]);
  const [inputPoiName, setInputPoiName] = useState('');
  const [customVariable, setCustomVariable] = useState({
    countryCity: '',
    productName: '',
    marketingTarget: ''
  });

  // Random Suggestions for Marketing Target
  const [randomSuggestions, setRandomSuggestions] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setRandomSuggestions(getRandomSuggestions(suggestions));
    }
  }, [isOpen]);

  const getRandomSuggestions = (suggestions) => {
    const shuffled = suggestions.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3); // Get 3 random suggestions
  };

  const handleCustomVariableChange = (e) => {
    const { name, value } = e.target;
    setCustomVariable(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      if (!inputValue) return callback([]);

      const filteredOptions = allCountries.reduce((acc, country) => {
        const countryName = country[0];
        const regions = country[2];
        if (countryName.toLowerCase().includes(inputValue.toLowerCase())) {
          acc.push({ label: countryName, value: countryName });
        }
        regions.forEach(region => {
          if (region[0].toLowerCase().includes(inputValue.toLowerCase())) {
            acc.push({ label: `${region[0]}, ${countryName}`, value: region[0] });
          }
        });
        return acc;
      }, []);

      callback(filteredOptions);
    }, 1000);
  };

  const handleSelectChange = (selectedOption) => {
    if (selectedOption) {
      setCustomVariable(prevState => ({
        ...prevState,
        countryCity: selectedOption.value 
      }));
    }
  };

  const handlePoiNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputPoiName && !poiNames.includes(inputPoiName) && poiNames.length < 5) {
        addPoiName();
      }
    }
  };

  const removePoiName = (nameToRemove) => {
    setPoiNames(poiNames.filter(name => name !== nameToRemove));
  };

  const addPoiName = () => {
    if (inputPoiName && !poiNames.includes(inputPoiName) && poiNames.length < 5) {
      setPoiNames([...poiNames, inputPoiName]);
      setInputPoiName('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateLoading(true);
    const reportPlanData = {
      reportName,
      language,
      poiType,
      poiNames,
      customVariable,
    };

    dispatch(createReportPlan({ audienceId: selectedAudience[0], reportPlanData }))
      .then((action) => {
        if (action.type.endsWith('fulfilled')) {
          showSuccessAlert('Success!', 'Your report request has been received!');
        }
      })
      .catch((error) => {
        console.error('An error occurred:', error);
        showErrorAlert('Error!', 'Unable to create report.');
      })
      .finally(() => {
        updateLoading(false);
      });
    toggle();
  };

  return (
    <>
      <Draggable handle=".modal-header">
        <Modal isOpen={isOpen} toggle={toggle} entered style={{ minWidth: '400px' }} className="modal-dialog modal-lg">
          <ModalHeader toggle={toggle} className="modal-header">Generate AI Report</ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="reportName">Report Name</Label>
                <Input id="reportName" name="reportName" value={reportName} onChange={(e) => setReportName(e.target.value)} required />
              </FormGroup>
              <FormGroup>
                <Label for="language">Language</Label>
                <Input
                  type="select"
                  name="languageValue"
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="form-control"
                >
                  {languageOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="poiType">POI Type</Label>
                <Input
                  type="select"
                  name="poiType"
                  id="poiType"
                  value={poiType}
                  onChange={(e) => setPoiType(e.target.value)}
                  className="form-control"
                >
                  {poiTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="poiNames">POI Names</Label>
                <div className="poi-name-tags" style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {poiNames.map((name, index) => (
                    <div key={index} className="tag" style={{ backgroundColor: '#cd1877', color: '#fff', border: '1px solid #cd1877', borderRadius: '4px', padding: '6px 12px', marginRight: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                      {name} <span className="remove-tag" onClick={() => removePoiName(name)}>×</span>
                    </div>
                  ))}
                </div>
                <InputGroup>
                  <Input
                    type="text"
                    name="poiNames"
                    id="poiNames"
                    placeholder={poiNames.length >= 5 ? "Max 5 POI names can be added" : "Enter a POI name and press Enter to add"}
                    value={inputPoiName}
                    onChange={(e) => setInputPoiName(e.target.value)}
                    onKeyPress={handlePoiNameKeyPress}
                    disabled={poiNames.length >= 5}
                  />
                  <Button
                    color="primary"
                    onClick={addPoiName}
                    disabled={poiNames.length >= 5}
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    Add
                  </Button>
                </InputGroup>
              </FormGroup>

              <FormGroup>
                <Label for="countryCity">Country/City</Label>
                <Select
                  className="custom-select"
                  cacheOptions
                  loadOptions={loadOptions}
                  defaultOptions
                  onChange={handleSelectChange}
                  placeholder="Select Country or City"
                  noOptionsMessage={({ inputValue }) => 
                    inputValue ? "No matching country or city found, please try a different search" : "Please start typing to search..."
                  }
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      width: '104%',
                      marginLeft: '-2%',
                      marginTop: '-2%',
                      borderRadius: '0.25rem',
                      borderColor: '#ced4da',
                      minHeight: 'calc(1.5em + 0.75rem + 2px)'
                    }),
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label for="productName">Product Name</Label>
                <Input id="productName" name="productName" placeholder="Producer of the Product and the Product/Service." value={customVariable.productName} onChange={handleCustomVariableChange} required />
              </FormGroup>

              <FormGroup>
                <Label for="marketingTarget">Marketing Target</Label>
                <Input id="marketingTarget" name="marketingTarget" placeholder="Marketing Target" value={customVariable.marketingTarget} onChange={handleCustomVariableChange} required />
              </FormGroup>

              {/* Random Suggestions */}
              <FormGroup>
                <Label className="form-label">Marketing Target Suggestions </Label>
                <ul>
                  {randomSuggestions.map((suggestion, index) => (
                    <li key={index}>
                      {suggestion}
                      <Button
                        color="link"
                        onClick={() => setCustomVariable(prevState => ({
                          ...prevState,
                          marketingTarget: suggestion
                        }))}
                      >
                        Apply
                      </Button>
                    </li>
                  ))}
                </ul>
              </FormGroup>

            </Form>
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn-light" onClick={toggle}>Close</button>
            <button type="submit" className="btn btn-success" id="addNewMember" onClick={handleSubmit}>Generate AI Report</button>
          </ModalFooter>
        </Modal>
      </Draggable>
    </>
  );
};

export default ReportCreateModal;
